/**
 * hooks/use-push.ts
 *
 * Handles Web Push subscription on behalf of the current user.
 *
 * Call `subscribeToPush()` to:
 *   1. Request notification permission if not yet granted.
 *   2. Subscribe via the Push API (VAPID public key from env).
 *   3. POST the subscription object to /push/subscribe.
 *
 * Call `sendTestNotification()` to:
 *   1. Call POST /push/test — sends a test push to your own device.
 *
 * Persists a per-user flag in localStorage so each account tracks its own
 * subscription state independently (important when multiple accounts share a
 * device — e.g. Account A then Account B logging in on the same browser).
 */
import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from '../lib/api-client'

const makePushKey = (userId: string) => `just-us:push-subscribed:${userId}`

const getVapidPublicKey = (): string | null =>
  (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? null

export type PushState = 'idle' | 'requesting' | 'subscribed' | 'denied' | 'unsupported'
export type TestState = 'idle' | 'sending' | 'sent' | 'error'

export const usePush = (userId: string | undefined) => {
  const [state, setState] = useState<PushState>('idle')
  const [testState, setTestState] = useState<TestState>('idle')

  // Determine initial state from stored flag + permission
  useEffect(() => {
    if (!userId) return

    if (
      !('Notification' in window) ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window)
    ) {
      setState('unsupported')
      return
    }
    if (Notification.permission === 'denied') {
      setState('denied')
      return
    }
    if (localStorage.getItem(makePushKey(userId)) === '1') {
      setState('subscribed')
    }
  }, [userId])

  const subscribeToPush = useCallback(async () => {
    if (!userId) return

    if (
      !('Notification' in window) ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window)
    ) {
      setState('unsupported')
      return
    }

    const vapidPublicKey = getVapidPublicKey()
    if (!vapidPublicKey) {
      console.warn('[push] VITE_VAPID_PUBLIC_KEY is not set — skipping push subscription')
      return
    }

    // Already subscribed on this device for this user
    if (localStorage.getItem(makePushKey(userId)) === '1') {
      setState('subscribed')
      return
    }

    // Request permission
    setState('requesting')
    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      setState('denied')
      return
    }

    try {
      const registration = await navigator.serviceWorker.ready

      // Convert VAPID public key from base64url to Uint8Array
      const padded = vapidPublicKey.replace(/-/g, '+').replace(/_/g, '/')
      const raw = Uint8Array.from(
        atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '=')),
        (c) => c.charCodeAt(0),
      )

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: raw,
      })

      // POST to API — store encrypted in D1 under this user's row
      await apiFetch('/push/subscribe', {
        method: 'POST',
        body: JSON.stringify(subscription.toJSON()),
      })

      localStorage.setItem(makePushKey(userId), '1')
      setState('subscribed')
    } catch (err) {
      console.warn('[push] Subscription failed:', err)
      setState('idle')
    }
  }, [userId])

  const sendTestNotification = useCallback(async () => {
    setTestState('sending')
    try {
      const res = (await apiFetch('/push/test', { method: 'POST' })) as Response
      if (res.ok) {
        setTestState('sent')
      } else {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }))
        console.warn('[push] Test notification failed:', (err as { error?: string }).error)
        setTestState('error')
      }
    } catch (err) {
      console.warn('[push] Test notification error:', err)
      setTestState('error')
    }
    setTimeout(() => setTestState('idle'), 3000)
  }, [])

  return { state, subscribeToPush, sendTestNotification, testState }
}
